import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, Star, X, ChevronRight, Coffee, UtensilsCrossed, Utensils, Plus, Loader2, Pencil } from 'lucide-react'
import { api } from '@/lib/api'
import { MacroBadgeRow } from './MacroBar'
import RecipeEditor from './RecipeEditor'
import type { Dish, SlotType } from '@/types/models'

interface Props {
  slotType: SlotType
  slotLabel: string
  onSelect: (dish: Dish) => void
  onSkip: (reason: string) => void
  onClose: () => void
}

const SKIP_REASONS = [
  { id: 'eating_out', label: '🍽️ Eating out' },
  { id: 'not_hungry', label: '😌 Not hungry' },
  { id: 'leftovers', label: '📦 Leftovers' },
  { id: 'fasting', label: '⏱️ Skipping / fasting' },
]

const SLOT_ICONS: Record<SlotType, React.ReactNode> = {
  meal: <Utensils className="w-4 h-4" />,
  snack: <UtensilsCrossed className="w-4 h-4" />,
  beverage: <Coffee className="w-4 h-4" />,
}

export default function DishPicker({ slotType, slotLabel, onSelect, onSkip, onClose }: Props) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<'favorites' | 'search' | 'skip'>('favorites')
  const [query, setQuery] = useState('')
  const [editingDish, setEditingDish] = useState<Dish | null>(null)

  // Create a custom dish (name-only) and auto-favorite it, then select it
  const addCustomDish = useMutation({
    mutationFn: async (name: string) => {
      const token = await getToken()
      const res = await api.post<{ data: Dish }>('/dishes', { name }, token!)
      const dish = (res as { data: Dish }).data
      await api.post(`/dishes/favorites/${dish.id}`, {}, token!)
      return dish
    },
    onSuccess: (dish) => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] })
      onSelect(dish)
    },
  })

  const { data: favsRes } = useQuery({
    queryKey: ['favorites'],
    queryFn: async () => {
      const token = await getToken()
      return api.get<{ data: Dish[] }>('/dishes/favorites', token!)
    },
  })
  const favorites = (favsRes as { data: Dish[] } | undefined)?.data ?? []

  const { data: searchRes, isLoading: searching } = useQuery({
    queryKey: ['dish-search', query, slotType],
    queryFn: async () => {
      if (!query.trim()) return { data: [] }
      const token = await getToken()
      return api.get<{ data: Dish[] }>(`/dishes/search?q=${encodeURIComponent(query)}&limit=20`, token!)
    },
    enabled: tab === 'search',
  })
  const searchResults = (searchRes as { data: Dish[] } | undefined)?.data ?? []

  const displayList = tab === 'favorites' ? favorites : searchResults

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-5 pb-3">
            <div className="flex items-center gap-2">
              <span className="text-gray-400">{SLOT_ICONS[slotType]}</span>
              <h3 className="font-bold text-gray-900">{slotLabel}</h3>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 px-5">
            {(['favorites', 'search', 'skip'] as const).map((t) => (
              <button key={t} onClick={() => setTab(t)}
                className={`py-2.5 px-4 text-sm font-medium border-b-2 transition-colors capitalize ${
                  tab === t
                    ? 'border-brand-600 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >{t === 'skip' ? 'Skip / Eating Out' : t}</button>
            ))}
          </div>

          {/* Search bar */}
          {tab === 'search' && (
            <div className="px-5 pt-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  autoFocus
                  placeholder="Search dishes…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="input pl-9"
                />
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-3">
            {/* Favorites / Search results */}
            {tab !== 'skip' && (
              <>
                {displayList.length === 0 && tab === 'favorites' && (
                  <div className="text-center py-10 text-gray-400">
                    <Star className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No favorites yet.</p>
                    <p className="text-xs mt-1">Switch to Search to find dishes.</p>
                  </div>
                )}
                {displayList.length === 0 && tab === 'search' && query && !searching && (
                  <div className="text-center py-8 space-y-3">
                    <p className="text-sm text-gray-400">No results for "{query}"</p>
                    <button
                      onClick={() => addCustomDish.mutate(query.trim())}
                      disabled={addCustomDish.isPending}
                      className="btn-primary inline-flex items-center gap-1.5 text-sm"
                    >
                      {addCustomDish.isPending
                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding…</>
                        : <><Plus className="w-4 h-4" /> Add "{query.trim()}" as new dish</>}
                    </button>
                    <p className="text-xs text-gray-400">Saves to your favourites automatically</p>
                  </div>
                )}
                {searching && (
                  <div className="space-y-2">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />
                    ))}
                  </div>
                )}
                <div className="space-y-2">
                  {displayList.map((dish) => (
                    <div key={dish.id}
                      className="w-full flex items-center p-3 rounded-xl border border-gray-200 hover:border-brand-400 hover:bg-brand-50 text-left transition-colors group cursor-pointer"
                      onClick={() => onSelect(dish)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{dish.name}</p>
                        <div className="mt-1">
                          <MacroBadgeRow
                            calories={dish.calories}
                            protein_g={dish.protein_g}
                            carbs_g={dish.carbs_g}
                            fat_g={dish.fat_g}
                            compact
                          />
                        </div>
                        {dish.cuisine_type && (
                          <p className="text-xs text-gray-400 mt-0.5">{dish.cuisine_type}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                        {/* Pencil icon for user's own custom dishes */}
                        {dish.created_by && tab === 'favorites' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingDish(dish) }}
                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Edit recipe"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-brand-500" />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Always show "add as custom" when there's a query and results exist */}
                {tab === 'search' && query.trim() && !searching && displayList.length > 0 && (
                  <button
                    onClick={() => addCustomDish.mutate(query.trim())}
                    disabled={addCustomDish.isPending}
                    className="w-full mt-1 flex items-center justify-center gap-1.5 py-2.5 text-sm text-brand-600 hover:bg-brand-50 rounded-xl border-2 border-dashed border-brand-200 transition-colors disabled:opacity-50"
                  >
                    {addCustomDish.isPending
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding…</>
                      : <><Plus className="w-4 h-4" /> Add "{query.trim()}" as custom dish</>}
                  </button>
                )}
              </>
            )}

            {/* Skip reasons */}
            {tab === 'skip' && (
              <div className="space-y-2 pt-1">
                <p className="text-sm text-gray-500 mb-3">
                  This meal won't be counted in macros or the shopping list.
                </p>
                {SKIP_REASONS.map((r) => (
                  <button key={r.id} onClick={() => onSkip(r.id)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 hover:border-brand-400 hover:bg-brand-50 text-sm font-medium text-gray-700 transition-colors"
                  >
                    {r.label}
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recipe editor — renders on top of the picker */}
      {editingDish && (
        <RecipeEditor
          dish={editingDish}
          onClose={() => setEditingDish(null)}
          onSaved={(updated) => {
            setEditingDish(null)
            queryClient.invalidateQueries({ queryKey: ['favorites'] })
            // Update the dish in the local display if needed
            setEditingDish(null)
          }}
        />
      )}
    </>
  )
}
